import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Progress, Card, PageLoad } from '../components/ui';

const FULL_DOC_PROGRAMS = ['BMT', 'AMT', 'ATM', 'BRM', 'MOA'];
const DOC_FIELDS = [
  { key: 'doc_first_aid',      label: 'First Aid Certificate',    fullDocOnly: true },
  { key: 'doc_pic',            label: 'Police Information Check', fullDocOnly: true },
  { key: 'doc_immunization',   label: 'Immunization Record',      fullDocOnly: true },
  { key: 'doc_resume',         label: 'Resume',                   fullDocOnly: false },
];
const COMPLETION_FIELDS = [
  { key: 'timesheet_uploaded',     label: 'Timesheet Uploaded' },
  { key: 'final_eval_uploaded',    label: 'Practicum Evaluation Uploaded' },
  { key: 'exit_survey_submitted',  label: 'Exit Survey Submitted' },
];

const STATUS_LABELS = {
  pre_req:    { label: 'Pre-Req', color: '#F59E0B', bg: '#FEF3C7' },
  ready:      { label: 'Ready',   color: '#3B82F6', bg: '#EFF6FF' },
  active:     { label: 'Active',  color: '#10B981', bg: '#ECFDF5' },
  completing: { label: 'Completing', color: '#8B5CF6', bg: '#F5F3FF' },
  complete:   { label: 'Complete',   color: '#059669', bg: '#D1FAE5' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function CheckItem({ done, label, pending }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
                  borderBottom: '1px solid var(--border)' }}>
      <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: done ? '#10B981' : 'var(--border)' }}>
        {done && (
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="#fff"
               strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="2,8 6,12 14,4" />
          </svg>
        )}
      </div>
      <span style={{ fontSize: 14, color: done ? 'var(--ink-1)' : 'var(--ink-3)', flex: 1 }}>
        {label}
      </span>
      {pending && !done && (
        <span style={{ fontSize: 11, background: '#FEF3C7', color: '#92400E',
                       padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>
          Pending
        </span>
      )}
    </div>
  );
}

export default function StudentPortalPage() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/auth/my-profile')
      .then(res => setProfile(res.data))
      .catch(() => setError('Failed to load your profile. Please try refreshing.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoad />;

  const initials = (
    (user.full_name?.split(' ')[0]?.[0] || '') +
    (user.full_name?.split(' ')[1]?.[0] || '')
  ).toUpperCase();

  const status = profile ? STATUS_LABELS[profile.status] : null;
  const requiredDocs = profile && FULL_DOC_PROGRAMS.includes(profile.program_code)
    ? DOC_FIELDS
    : DOC_FIELDS.filter(d => !d.fullDocOnly);

  const hoursLogged = parseFloat(profile?.hours_logged || 0);
  const hoursRequired = profile?.hours_required || 0;
  const hoursRemaining = Math.max(0, hoursRequired - hoursLogged);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>

      {/* ── Top nav ── */}
      <div style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border)',
                    padding: '0 32px', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', height: 60, position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/mcg-logo.png" alt="MCG" style={{ height: 32 }} />
          <span style={{ fontSize: 13, color: 'var(--ink-3)', fontWeight: 500, borderLeft: '1px solid var(--border)', paddingLeft: 12 }}>
            Student Portal
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{user.full_name}</span>
          <button className="btn btn-ghost btn-sm" onClick={logout}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: 840, margin: '0 auto', padding: '40px 24px 80px' }}>
        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C',
                        borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 14 }}>
            {error}
          </div>
        )}

        {profile && (
          <>
            {/* ── Profile header ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 32 }}>
              <div style={{ width: 68, height: 68, borderRadius: '50%',
                            background: 'var(--brand-gold, #DE9842)', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: 24, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--ink-1)', lineHeight: 1.2 }}>
                  {user.full_name}
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink-3)', marginTop: 4 }}>
                  {profile.program_name || profile.program_code || 'Program not assigned'}
                  {profile.campus ? ` · ${profile.campus}` : ''}
                </div>
              </div>
              {status && (
                <div style={{ padding: '6px 14px', borderRadius: 99, fontWeight: 600,
                              fontSize: 13, background: status.bg, color: status.color }}>
                  {status.label}
                </div>
              )}
            </div>

            {/* ── Timeline ── */}
            <Card title="Practicum Timeline" style={{ marginBottom: 20 }}>
              <div className="form-grid-2" style={{ gap: '16px 32px', padding: '4px 0 8px' }}>
                {[
                  { label: 'Cohort Start',          value: fmtDate(profile.cohort_start_date) },
                  { label: 'Expected Graduation',    value: fmtDate(profile.cohort_end_date) },
                  { label: 'Practicum Start',        value: fmtDate(profile.practicum_start_date) },
                  { label: 'Host Organization',      value: profile.host_name || 'Not yet assigned' },
                  { label: 'Practicum Coordinator',  value: profile.pc_name  || 'Not yet assigned' },
                  { label: 'Campus Login ID',        value: profile.campus_login_id || '—' },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)',
                                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 15, color: 'var(--ink-1)', fontWeight: 500 }}>{value}</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* ── Hours & milestones ── */}
            <Card title="Hours Progress" style={{ marginBottom: 20 }}>
              <div style={{ padding: '4px 0 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'baseline', marginBottom: 10 }}>
                  <span style={{ fontSize: 40, fontWeight: 700, color: 'var(--ink-1)',
                                 lineHeight: 1 }}>{hoursLogged}</span>
                  <span style={{ fontSize: 14, color: 'var(--ink-3)' }}>
                    / {hoursRequired} hrs required
                  </span>
                </div>
                <Progress value={hoursLogged} max={hoursRequired}
                          variant={profile.status === 'complete' ? 'green' : ''} />
                <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8 }}>
                  {hoursRemaining > 0
                    ? `${hoursRemaining.toFixed(1)} hours remaining`
                    : '✓ All hours complete!'}
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)',
                              textTransform: 'uppercase', letterSpacing: '0.06em',
                              padding: '10px 0 4px' }}>Milestones</div>
                {COMPLETION_FIELDS.map(f => (
                  <CheckItem key={f.key} done={!!profile[f.key]} label={f.label} />
                ))}
              </div>
            </Card>

            {/* ── Documents ── */}
            <Card title="Pre-Req Documents"
                  sub={`${requiredDocs.filter(d => profile[d.key]).length} / ${requiredDocs.length} received`}>
              {requiredDocs.map(d => (
                <CheckItem key={d.key} done={!!profile[d.key]} label={d.label} pending />
              ))}
              {requiredDocs.every(d => profile[d.key]) && (
                <div style={{ marginTop: 16, fontSize: 14, color: '#059669', fontWeight: 500 }}>
                  ✓ All required documents received.
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
